// STRK-03 (stille nullstilling): denne fila skal ALDRI vise noe som forteller
// barnet at streaken ble borte. Det eneste tegnet på en nullstilling er at
// flisa for «på rad nå» sier «Start i dag», og neste dag med aktivitet en
// «Ny start! 1 dag»-chip i 4 s. Ordlyden skal være positiv. Regelen fulgte med
// fra StreakHero, som denne fila erstatter (#156, steg 6e).

/**
 * WalletStats — 2×2-statistikken på Lommebok (#156, steg 6e; Stell_Lommebok).
 *
 * Erstatter StreakHero. Fire fliser med strektegning i hver sin tone:
 *   - på rad nå      `current_streak`          lime
 *   - beste streak   `longest_streak`          warn
 *   - frysning       `streak_freezes_balance`  blue. «ny mandag» stemmer:
 *                    streaks.service deler ut én frysning mandag 00:00 (Oslo),
 *                    med tak på én;
 *   - beste uke      `usePersonalBest`         lilac.
 *
 * Alt leses fra API-et ved visning; ingenting lagres ved siden av. Ukjent verdi
 * (henter eller feilet) er en strek, ikke 0. Ingen personlig best (ingen
 * opptjente poeng ennå) er 0 p.
 *
 * «Ny start!»-chipen er den samme engangsvarselen som StreakHero hadde: bare når
 * streaken er 1 og siste aktivitet var i dag, én gang per dag (sessionStorage).
 */
import { format } from 'date-fns';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, type IconName } from '../../components/primitives/Icon';
import { GLASS_SURFACE } from '../../components/primitives/Card';
import { formatPoints } from '../../i18n/formatters';
import { useStreak, type StreakRow } from '../streaks/useStreakQueries';
import { usePersonalBest } from './usePointsQueries';

const NY_START_VISIBLE_MS = 4000;

type Tone = 'lime' | 'warn' | 'blue' | 'lilac';

const TONE_CLASS: Record<Tone, string> = {
  lime: 'bg-lime-bg text-lime',
  warn: 'bg-warn-bg text-warn',
  blue: 'bg-blue-bg text-blue',
  lilac: 'bg-lilac-bg text-lilac',
};

function StatTile({
  icon,
  tone,
  value,
  label,
  'data-testid': testId,
}: {
  icon: IconName;
  tone: Tone;
  value: string;
  label: string;
  /** Bokstavelig på kallstedet, så testid-kontrakten ser den. */
  'data-testid': string;
}): ReactNode {
  const Glyph = Icon[icon];
  return (
    <li data-testid={testId} className={`${GLASS_SURFACE} flex items-center gap-2.5 rounded-row px-3.5 py-3 shadow-md`}>
      <span aria-hidden className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-sm ${TONE_CLASS[tone]}`}>
        <Glyph size={18} />
      </span>
      <span className="min-w-0">
        <span className="tnum block text-base font-extrabold text-ink">{value}</span>
        <span className="block text-label text-ink-2">{label}</span>
      </span>
    </li>
  );
}

/** Dagens dato på enheten (Oslo), som `last_activity_date`. Ikke UTC-datoen. */
function todayIsoDate(): string {
  return format(new Date(), 'yyyy-MM-dd');
}

/** Engangsvarselet «Ny start!», én gang per medlem og dag. */
function shouldShowNyStart(data: StreakRow | undefined, memberId: string): boolean {
  if (!data) return false;
  if (data.current_streak !== 1) return false;
  if (!data.last_activity_date) return false;
  if (data.last_activity_date !== todayIsoDate()) return false;
  try {
    const key = `ny-start-shown:${memberId}:${data.last_activity_date}`;
    if (globalThis.sessionStorage?.getItem(key) === '1') return false;
    globalThis.sessionStorage?.setItem(key, '1');
  } catch {
    // Uten sessionStorage (privat modus) vises chipen likevel, én gang.
  }
  return true;
}

function NyStartChip({ current }: { current: number }): ReactNode {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    const id = setTimeout(() => setVisible(false), NY_START_VISIBLE_MS);
    return () => clearTimeout(id);
  }, []);
  if (!visible) return null;
  return (
    <span
      data-testid="streak-hero-ny-start"
      role="status"
      aria-live="polite"
      className="inline-flex items-center gap-1 self-start rounded-pill bg-lime-bg px-3 py-1.5 text-sm font-bold text-lime"
    >
      <Icon.Sparkle size={14} className="shrink-0" />
      <span>{t('streaks.nyStart', { count: current })}</span>
    </span>
  );
}

export function WalletStats({ memberId }: { memberId: string }): ReactNode {
  const { t } = useTranslation();
  const { data: streak } = useStreak(memberId);
  const best = usePersonalBest(memberId);
  // Beregnes én gang per montering, så sessionStorage-flagget ikke skrus av
  // chipen ved neste render.
  const [nyStart, setNyStart] = useState(false);
  useEffect(() => {
    if (shouldShowNyStart(streak, memberId)) setNyStart(true);
  }, [streak, memberId]);

  const pending = t('rewards.wallet.pending');
  const days = (n: number): string => t('points.stats.days', { count: n });
  const current = streak?.current_streak;

  const bestWeek = best.isSuccess ? t('rewards.points', { points: formatPoints(best.data?.points ?? 0) }) : pending;

  return (
    <>
      <ul data-testid="wallet-stats" aria-label={t('points.stats.label')} className="m-0 grid list-none grid-cols-2 gap-2 p-0">
        <StatTile
          data-testid="wallet-stat-streak"
          icon="StatStreak"
          tone="lime"
          value={current === undefined ? pending : current > 0 ? days(current) : t('points.stats.streak.none')}
          label={current === 0 ? t('points.stats.streak.noneLabel') : t('points.stats.streak.label')}
        />
        <StatTile
          data-testid="wallet-stat-longest"
          icon="StatBest"
          tone="warn"
          value={streak ? days(streak.longest_streak) : pending}
          label={t('points.stats.longest')}
        />
        <StatTile
          data-testid="wallet-stat-freezes"
          icon="StatFreeze"
          tone="blue"
          value={streak ? t('points.stats.freezes.value', { count: streak.streak_freezes_balance }) : pending}
          label={t('points.stats.freezes.label')}
        />
        <StatTile
          data-testid="wallet-stat-best-week"
          icon="StatWeek"
          tone="lilac"
          value={bestWeek}
          label={t('points.stats.bestWeek')}
        />
      </ul>
      {nyStart && current !== undefined ? <NyStartChip current={current} /> : null}
    </>
  );
}
